import type { ConsoleDashboard, DashboardSezione, DashboardWidget } from '../../api/types'
import { t } from '../../i18n/lingua'
import { CATALOGO, type DefinizioneWidget } from './catalogo'

/**
 * La forma di una dashboard e la dashboard **predefinita**: quella che si vede
 * la prima volta, costruita con i widget che la persona può vedere (moduli e
 * licenze), divisa per sezioni. Non si salva finché non la si personalizza.
 */

export function idNuovo(): string {
  return Math.random().toString(36).slice(2, 10)
}

export function widgetDa(def: DefinizioneWidget): DashboardWidget {
  const options: Record<string, unknown> = {}
  for (const o of def.opzioni ?? []) options[o.key] = o.predefinito
  return { id: idNuovo(), type: def.tipo, size: def.size, tall: !!def.tall, options }
}

export function dashboardPredefinita(visibile: (def: DefinizioneWidget) => boolean): ConsoleDashboard {
  const prendi = (tipi: string[]) =>
    tipi.map((tp) => CATALOGO.find((d) => d.tipo === tp)).filter((d): d is DefinizioneWidget => !!d && visibile(d)).map(widgetDa)
  const sezioni: DashboardSezione[] = [
    { id: 'panoramica', title: t('Panoramica'), widgets: prendi(['ci-gestiti', 'host-non-gestiti', 'sonde-online', 'job-falliti', 'ci-tipo', 'movimenti', 'scansioni', 'job-stati', 'sonde-stati']) },
    { id: 'traffico', title: t('Traffico'), widgets: prendi(['traffico-volume', 'top-talkers', 'top-conversazioni', 'top-servizi', 'top-protocolli', 'esportatori', 'entry-points']) },
    { id: 'sicurezza', title: t('Sicurezza'), widgets: prendi(['rischio', 'dobermann', 'wazuh', 'end-of-support', 'dobermann-talkers', 'honeypot', 'reperti']) },
    { id: 'protezione', title: t('Protezione e capacità'), widgets: prendi(['backup', 'backup-vm', 'asset', 'capacita-storage', 'capacita-compute', 'costi-mese', 'costi-servizi']) },
    { id: 'eagle', title: 'Eagle', widgets: prendi(['eagle-problemi', 'eagle-stati', 'eagle-allarmi', 'eagle-elenco']) },
  ].filter((s) => s.widgets.length > 0)
  return { id: null, name: t('La mia dashboard'), kind: 'home', shared: false, sections: sezioni, rotation_s: 30, updated_at: null, locked: false, can_edit: true }
}

/** Il cruscotto di Eagle la prima volta: stato, andamento, risorse, sonde e SLA. */
export function dashboardPredefinitaEagle(visibile: (def: DefinizioneWidget) => boolean): ConsoleDashboard {
  const prendi = (tipi: string[]) =>
    tipi.map((tp) => CATALOGO.find((d) => d.tipo === tp)).filter((d): d is DefinizioneWidget => !!d && visibile(d)).map(widgetDa)
  // Stato: i numeri con l'andamento, la parete, i problemi aperti e gli
  // allarmi nel tempo. Andamento: incidenti e disponibilità, parametri per
  // stato, SLA. Risorse: le classifiche. Sonde: chi legge e con quale carico.
  // Nessun widget due volte: lo stesso grafico in due sezioni non dice di più.
  const sezioni: DashboardSezione[] = [
    { id: 'stato', title: t('Stato'), widgets: prendi(['eagle-hero', 'eagle-parete', 'eagle-elenco', 'eagle-allarmi']) },
    { id: 'andamento', title: t('Andamento'), widgets: prendi(['eagle-incidenti', 'eagle-disponibilita', 'eagle-stati', 'eagle-sla']) },
    { id: 'risorse', title: t('Risorse'), widgets: [
      ...prendi(['eagle-top']),
      ...prendi(['eagle-top']).map((w) => ({ ...w, id: idNuovo(), options: { ...w.options, kind: 'memory' } })),
      ...prendi(['eagle-top']).map((w) => ({ ...w, id: idNuovo(), options: { ...w.options, kind: 'volume' } })),
      ...prendi(['eagle-top']).map((w) => ({ ...w, id: idNuovo(), options: { ...w.options, kind: 'interface' } })),
    ] },
    { id: 'sonde', title: t('Sonde'), widgets: prendi(['eagle-sonde']) },
  ].filter((s) => s.widgets.length > 0)
  return { id: null, name: t('Cruscotto di Eagle'), kind: 'eagle', shared: false, sections: sezioni, rotation_s: 30, updated_at: null, locked: false, can_edit: true }
}

/**
 * The wall display of Eagle, as it was before it became a dashboard: the
 * wall of CIs across the screen, then the open problems, the alarms over
 * time and the alarm history side by side. One section, no rotation: a
 * wall stays still. Everything resizable from «Personalizza».
 */
export function dashboardPredefinitaSinottico(visibile: (def: DefinizioneWidget) => boolean): ConsoleDashboard {
  const prendi = (tipo: string, size?: DashboardWidget['size'], tall = true): DashboardWidget[] => {
    const def = CATALOGO.find((d) => d.tipo === tipo)
    if (!def || !visibile(def)) return []
    const w = widgetDa(def)
    return [{ ...w, size: size ?? w.size, tall }]
  }
  const sezioni: DashboardSezione[] = [{
    id: 'parete', title: t('Parete'),
    widgets: [
      // The wall pages every 15 seconds: a monitor has no one to scroll it.
      ...prendi('eagle-parete', 'xl').map((w) => ({ ...w, options: { ...w.options, pagine_s: '15' } })),
      ...prendi('eagle-elenco', 'm'), ...prendi('eagle-allarmi', 'm'), ...prendi('eagle-storico', 'm'),
    ],
  }].filter((s) => s.widgets.length > 0)
  return { id: null, name: t('Sinottico di Eagle'), kind: 'eagle_wall', shared: false, sections: sezioni, rotation_s: 0, updated_at: null, locked: false, can_edit: true }
}

/**
 * The widgets' pictures travel apart from the dashboard: in `images`, one
 * JSON text «widget id → data URL», while the widgets keep every other
 * option. Inside `sections[].widgets[].options.image` the argument name
 * changes with the list indices and the WAF cannot exempt it by name; a
 * single field with a fixed name can be (exclusion 1022). The Control Plane
 * puts them back (`console_dashboard_service._innesta_immagini`), so the
 * stored dashboard is the same as before.
 */
export function impacchettaImmagini(sections: DashboardSezione[]): { sections: DashboardSezione[]; images?: string } {
  const immagini: Record<string, string> = {}
  const pulite = sections.map((s) => ({
    ...s,
    widgets: s.widgets.map((w) => {
      const img = w.options?.image
      if (typeof img !== 'string' || !img.startsWith('data:')) return w
      immagini[w.id] = img
      const { image: _scartata, ...resto } = w.options ?? {}
      void _scartata
      return { ...w, options: resto }
    }),
  }))
  return Object.keys(immagini).length ? { sections: pulite, images: JSON.stringify(immagini) } : { sections }
}

